import {
  Component,
  AfterContentInit,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
  Input,
  TemplateRef,
  Renderer2,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  ViewEncapsulation,
  SimpleChanges,
  OnChanges,
  ContentChildren,
  QueryList,
  ComponentRef,
  ViewContainerRef,
  Injector,
  NgModuleRef,
  createNgModule,
} from '@angular/core';
import { PhxCarouselItemDirective } from './phx-carousel-item.directive';
import { FullScreen } from '../icon/icon-map';
import { PhxGalleryImage } from '../gallery/phx-gallery.type';
import { PhxPaginationResponse } from '../pagination/phx-pagination.types';
import type { PhxGalleryComponent } from '../gallery/phx-gallery.component';

const DEBOUNCE_TIMEOUT = 300;


@Component({
  selector: 'phx-carousel',
  templateUrl: './phx-carousel.component.html',
  styleUrls: ['./phx-carousel.component.scss'],
  host: { class: 'phx-carousel' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class PhxCarouselComponent implements AfterContentInit, OnDestroy, OnChanges {
  @Input() public phxItemsToShow: number | null = null;
  @Input() public phxItemsToScroll: number = 1;
  @Input() public phxCircular: boolean = false;
  @Input() public phxItemWidth: string = '150px';
  @Input() public phxLeftNavigationTemplate: TemplateRef<any>;
  @Input() public phxRightNavigationTemplate: TemplateRef<any>;
  // Photo overlay variant: bottom-right pagination + bottom-left enlarge button.
  @Input() public phxShowPagination: boolean = false;
  @Input() public phxShowExpandButton: boolean = false;
  @Input() public phxGalleryImages: PhxGalleryImage[] = [];

  @Output() public phxOnSlide = new EventEmitter<void>();

  @ContentChildren(PhxCarouselItemDirective, { descendants: true }) directiveItems!: QueryList<PhxCarouselItemDirective>;

  @ViewChild('scrollContainer', { static: true }) scrollContainer!: ElementRef<HTMLElement>;
  @ViewChild('innerTrack', { static: true }) carouselInnerTrackElementRef!: ElementRef<HTMLElement>;
  @ViewChild('galleryHost', { read: ViewContainerRef }) private galleryHost!: ViewContainerRef;

  public hasMoreItems = true;
  public items: PhxCarouselItemDirective[] = [];
  public readonly fullScreenIcon: string = FullScreen;

  private _phxItemsToShow: number | null = null;
  private itemWidthPx: number;
  private isScrollingCarousel = true;
  private currentStartIndex = 0;
  private galleryRef?: ComponentRef<PhxGalleryComponent>;
  private galleryRefPromise?: Promise<ComponentRef<PhxGalleryComponent>>;
  private galleryModuleRef?: NgModuleRef<unknown>;

  private resizeObserver?: ResizeObserver;
  private scrollDebounceTimeout: ReturnType<typeof setTimeout> | null;
  private containerElement: HTMLElement;
  private boundOnScroll: EventListener | null = null;
  private unlistenScroll: (() => void) | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private host: ElementRef,
    private renderer: Renderer2,
    private injector: Injector,
  ) {}

  ngAfterContentInit(): void {
    this.isScrollingCarousel = !this._phxItemsToShow;
    this.containerElement = this.scrollContainer.nativeElement as HTMLElement;
    this.itemWidthPx = parseFloat(this.phxItemWidth);
    this.items = this.directiveItems?.toArray() || [];
  }

  ngAfterViewInit(): void {
    if(this.items && this.items.length > 0) {
      this.setupResizeObserver();
      this.setHasMoreItems();
      this.setMouseScrolling();
      if(this.currentStartIndex >= this.items.length) {
        this.currentStartIndex = 0;
      }
      this.updateItemsToShow();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.unlistenScroll?.();
    this.galleryRef?.destroy();
    this.galleryModuleRef?.destroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { phxGalleryImages, phxItemsToShow, phxItemWidth, phxShowPagination } = changes;

    if (this.phxShowPagination) {
      this._phxItemsToShow = 1;
    } else {
      this._phxItemsToShow = this.phxItemsToShow;
    }

    if((phxItemsToShow && phxItemsToShow?.currentValue) || phxShowPagination) {
      this.isScrollingCarousel = !this._phxItemsToShow;
    }

    if(phxItemWidth && phxItemWidth?.currentValue) {
      this.itemWidthPx = parseFloat(phxItemWidth.currentValue);
    }

    if (this.galleryRef && phxGalleryImages) {
      this.galleryRef.setInput('phxImages', this.phxGalleryImages);
    }
  }

  /** 0-based index of the first visible item; drives the pagination counter and gallery seed. */
  public get currentIndex(): number {
    return this.currentStartIndex;
  }

  /** Pagination overlay is shown only when enabled and there is more than one item to page through. */
  public get showPaginationOverlay(): boolean {
    return this.phxShowPagination && this.items.length > 1;
  }

  public get canPrev(): boolean {
    if (!this.items || this.items.length === 0) return false;
    // if circular and there are items, show prev button when carousel is scrollable
    if (this.phxCircular && this.hasMoreItems) return true;
    if (!this.isScrollingCarousel) {
      // windowed mode: canPrev if start index > 0 (or circular handled above)
      return this.currentStartIndex > 0;
    }
    return this.containerElement.scrollLeft > 0;
  }

  public get canNext(): boolean {
    if (!this.items || this.items.length === 0) return false;
    if (this.phxCircular && this.hasMoreItems) return true;
    if (!this.isScrollingCarousel) {
      // windowed mode: canNext if there remains items to the right
      const n = this.items ? this.items.length : 0;
      const maxStart = Math.max(0, n - (this._phxItemsToShow || 1));
      return this.currentStartIndex < maxStart;
    }
    return this.containerElement.scrollLeft + this.containerElement.clientWidth < this.containerElement.scrollWidth;
  }

  public onPrev(): void {
    if (!this.items || this.items.length === 0) return;

    // If mouse scrolling is enabled, behave as before (scroll the container)
    if (this.isScrollingCarousel) {
      const maxLeft = Math.max(0, this.containerElement.scrollWidth - this.containerElement.clientWidth);

      let left: number;
      if (this.phxCircular) {
        // wrap to end when at or before start
        if (this.containerElement.scrollLeft <= 0) {
          left = maxLeft;
        } else {
          left = Math.max(0, this.scrollBy(-1));
        }
      } else {
        left = this.scrollBy(-1);
      }

      this.containerElement.scrollTo({ left, behavior: 'smooth' });
    } else {
      // windowed mode: move the start index left
      this.updateStartIndex(-1);
    }

    this.phxOnSlide.emit();
    // clear any previous debounce timer
    if (this.scrollDebounceTimeout) clearTimeout(this.scrollDebounceTimeout);
    this.scrollDebounceTimeout = setTimeout(() => {
      this.cdr.detectChanges();
    }, DEBOUNCE_TIMEOUT);
  }

  public onNext(): void {
    if (!this.items || this.items.length === 0) return;

    if (this.isScrollingCarousel) {
      const maxLeft = Math.max(0, this.containerElement.scrollWidth - this.containerElement.clientWidth);
      
      let left: number;
      if (this.phxCircular) {
        // wrap to start when at or beyond end
        if (this.containerElement.scrollLeft + this.containerElement.clientWidth >= this.containerElement.scrollWidth) {
          left = 0;
        } else {
          left = Math.min(maxLeft, this.scrollBy(1));
        }
      } else {
        left = this.scrollBy(1);
      }

      this.containerElement.scrollTo({ left, behavior: 'smooth' });
    } else {
      // windowed mode: move the start index right
      this.updateStartIndex(1);
    }

    this.phxOnSlide.emit();
    // clear any previous debounce timer
    if (this.scrollDebounceTimeout) clearTimeout(this.scrollDebounceTimeout);
    this.scrollDebounceTimeout = setTimeout(() => {
      this.cdr.detectChanges();
    }, DEBOUNCE_TIMEOUT);
  }

  public scrollToLast(): void {
    if (!this.items || this.items.length === 0 || !this.isScrollingCarousel) return;
    const maxLeft = Math.max(0, this.containerElement.scrollWidth - this.containerElement.clientWidth);
    this.containerElement.scrollTo({ left: maxLeft,  behavior: 'smooth' });
    this.phxOnSlide.emit();
  }

  public scrollToIndex(index: number): void {
    if (!this.items || this.items.length === 0 || !this.isScrollingCarousel) return;
    const validIndex = Math.max(0, Math.min(index, this.items.length - 1));
    const gap = this.calculateGap(this.carouselInnerTrackElementRef?.nativeElement);
    const left = validIndex * (this.itemWidthPx + gap);
    this.containerElement.scrollTo({ left, behavior: 'smooth' });
    this.phxOnSlide.emit();
  }

  /**
   * Jump to an arbitrary index. Works in both scroll and windowed modes
   * (unlike `scrollToIndex`, which only applies to scroll mode).
   */
  public goToIndex(index: number): void {
    if (!this.items || this.items.length === 0) return;
    const validIndex = Math.max(0, Math.min(index, this.items.length - 1));
    if (this.isScrollingCarousel) {
      this.scrollToIndex(validIndex);
      return;
    }
    this.currentStartIndex = validIndex;
    this.updateItemsToShow();
    this.phxOnSlide.emit();
  }

  public onPaginationNavigate(response: PhxPaginationResponse): void {
    if (response.isNextPage) {
      this.onNext();
    } else {
      this.onPrev();
    }
  }

  public async onExpand(): Promise<void> {
    if (!this.galleryRefPromise) {
      this.galleryRefPromise = this.createAndWireGallery();
    }

    try {
      const ref = await this.galleryRefPromise;
      this.openGalleryRef(ref);
    } catch {
      this.galleryRefPromise = undefined;
    }
  }

  private async createAndWireGallery(): Promise<ComponentRef<PhxGalleryComponent>> {
    const ref = await this.createGallery();
    ref.setInput('phxImages', this.phxGalleryImages);
    ref.setInput('phxEditMode', false);
    ref.instance.phxImageNavigate.subscribe((index: number) => this.onGalleryNavigate(index));
    ref.instance.phxOpenChange.subscribe((open: boolean) => {
      if (!open) {
        this.galleryRef?.destroy();
        this.galleryRef = undefined;
        this.galleryRefPromise = undefined;
      }
    });
    this.galleryRef = ref;
    return ref;
  }

  public onGalleryNavigate(index: number): void {
    this.goToIndex(index);
  }

  private openGalleryRef(ref: ComponentRef<PhxGalleryComponent>): void {
    ref.setInput('phxSelectedIndex', this.currentStartIndex);
    ref.setInput('phxOpen', true);
    ref.changeDetectorRef.detectChanges();
  }

  /**
   * Dynamically imports the gallery (emitted as a separate lazy chunk, never bundled
   * with the carousel) and creates a view-only instance. The gallery module is
   * instantiated so its providers (translations, pan-zoom, etc.) resolve — the
   * component is created with that module ref, otherwise its DI would resolve against
   * the carousel's injector and throw (e.g. missing TRANSLOCO_TRANSPILER).
   */
  protected async createGallery(): Promise<ComponentRef<PhxGalleryComponent>> {
    const [{ PhxGalleryComponent: GalleryComponent }, { PhxGalleryModule }] = await Promise.all([
      import('../gallery/phx-gallery.component'),
      import('../gallery/phx-gallery.module'),
    ]);
    if (!this.galleryModuleRef) {
      this.galleryModuleRef = createNgModule(PhxGalleryModule, this.injector);
    }
    return this.galleryHost.createComponent(GalleryComponent, { ngModuleRef: this.galleryModuleRef });
  }

  private scrollBy(direction: number): number {
    const gap = this.calculateGap(this.carouselInnerTrackElementRef?.nativeElement);
    return this.containerElement.scrollLeft + (direction * ((this.itemWidthPx + gap) * this.phxItemsToScroll));
  }

  private calculateGap(element: HTMLElement): number {
    if(!element) return 0;
    const style = getComputedStyle(element);
    const gapVal = style.gap || style.columnGap || '0px';
    return parseFloat(gapVal) || 0;
  }

  private updateStartIndex(direction: number): void {
    const itemsCount = this.items ? this.items.length : 0;
    if (itemsCount === 0) return;
    const step = Math.max(1, this.phxItemsToScroll || 1);
    if (this.phxCircular) {
      this.currentStartIndex = (this.currentStartIndex + (direction * step)) % itemsCount;
      if (this.currentStartIndex < 0) this.currentStartIndex += itemsCount;
    } else if (direction < 0) {
      this.currentStartIndex = Math.max(0, this.currentStartIndex - step);
    } else {
      const maxStart = Math.max(0, itemsCount - (this._phxItemsToShow || 1));
      this.currentStartIndex = Math.min(maxStart, this.currentStartIndex + step);
    }
    this.updateItemsToShow();
  }

  private onScroll(): void {
    if (this.scrollDebounceTimeout) clearTimeout(this.scrollDebounceTimeout);
    this.scrollDebounceTimeout = setTimeout(() => {
      this.cdr.detectChanges();
    }, DEBOUNCE_TIMEOUT);
  }

  private setHasMoreItems(): void {
    if (!this.host || !this.host.nativeElement || !this.scrollContainer || !this.containerElement) return;
    const hostEl = this.host.nativeElement as HTMLElement;
    const hostWidth = hostEl.clientWidth;

    const trackWidth = this.carouselInnerTrackElementRef?.nativeElement.offsetWidth || 0;

    this.hasMoreItems = trackWidth > hostWidth || !this.isScrollingCarousel;

    if (this._phxItemsToShow) {
      const showWidth = this.itemWidthPx * this._phxItemsToShow + this.calculateGap(this.carouselInnerTrackElementRef?.nativeElement) * Math.max(0, this._phxItemsToShow - 1);
      if (showWidth > hostWidth) {
        this.isScrollingCarousel = true;
      }
    }
  }

  /**
   * Update the `itemsToShow` array based on the current `currentStartIndex` and `phxItemsToShow`.
   * This is used when `isScrollingCarousel` is false to render a fixed window of items.
   */
  private updateItemsToShow(): void {
    // Instead of re-rendering templates, show/hide projected item elements so we keep original DOM
    const itemsArray = this.items ?? [];
    const itemsCount = itemsArray.length;
    const count = Math.max(1, this._phxItemsToShow || 1);

    // Clamp and normalize start index
    if (itemsCount === 0) return;
    if (this.currentStartIndex < 0) this.currentStartIndex = 0;
    if (this.currentStartIndex >= itemsCount) this.currentStartIndex = Math.max(0, itemsCount - 1);

    // Show or hide native elements
    itemsArray.forEach((element : PhxCarouselItemDirective, index: number) => {
      if (!element) return;
      const nativeElement : HTMLElement = element?.el?.nativeElement;
      if (!nativeElement) return;
      // remove any prev/next marker classes first to keep state consistent
      this.renderer.removeClass(nativeElement, 'phx-carousel-item-prev');
      this.renderer.removeClass(nativeElement, 'phx-carousel-item-next');

      let isVisible: boolean;
      if (this.phxCircular && count < itemsCount) {
        const distance = (index - this.currentStartIndex + itemsCount) % itemsCount;
        isVisible = distance < count;
      } else {
        isVisible = index >= this.currentStartIndex && index < this.currentStartIndex + count;
      }

      if (this.isScrollingCarousel) {
        // keep elements in the DOM so CSS transitions can run when classes change
        this.renderer.removeAttribute(nativeElement, 'aria-hidden');
        this.renderer.removeClass(nativeElement, 'phx-carousel-item-visible');
      } else {
        if (isVisible) {
          // mark visible: let CSS handle opacity/transform transitions
          this.renderer.removeAttribute(nativeElement, 'aria-hidden');
          this.renderer.addClass(nativeElement, 'phx-carousel-item-visible');
        } else {
          // keep offscreen items in DOM but mark as hidden for accessibility
          this.renderer.setAttribute(nativeElement, 'aria-hidden', 'true');
          this.renderer.removeClass(nativeElement, 'phx-carousel-item-visible');
        }
      }
    });

    // Add prev/next marker classes around the visible window when in windowed mode
      // clear previous markers already removed above; only add when not mouse scrolling
    if (!this.isScrollingCarousel && itemsCount > 0) {
      // if all items are visible, there's no neighbor to mark
      this.renderer.addClass(this.containerElement, 'phx-carousel-windowed');
      if (count < itemsCount) {
        const firstVisibleIndex = this.currentStartIndex;
        const lastVisibleIndex = this.phxCircular ? ((this.currentStartIndex + count - 1) % itemsCount) : Math.min(itemsCount - 1, this.currentStartIndex + count - 1);

        // compute left neighbor
        let leftIdx: number | null = firstVisibleIndex - 1;
        if (leftIdx < 0) leftIdx = this.phxCircular ? itemsCount - 1 : null;

        // compute right neighbor (element after the last visible)
        let rightIdx: number | null = lastVisibleIndex + 1;
        if (rightIdx >= itemsCount) rightIdx = this.phxCircular ? 0 : null;

        if (leftIdx !== null && leftIdx >= 0 && leftIdx < itemsCount) {
          const dir = itemsArray[leftIdx];
          if (dir && dir?.el?.nativeElement) this.renderer.addClass(dir.el.nativeElement, 'phx-carousel-item-prev');
        }
        if (rightIdx !== null && rightIdx >= 0 && rightIdx < itemsCount) {
          const dir = itemsArray[rightIdx];
          if (dir && dir?.el?.nativeElement) this.renderer.addClass(dir.el.nativeElement, 'phx-carousel-item-next');
        }
      }
    }
    // run change detection to update view
    this.cdr.detectChanges();
  }

  private setMouseScrolling(): void {
    if(!this.scrollContainer || !this.containerElement) return;
    if (this.boundOnScroll) {
      try {
        this.containerElement.removeEventListener('scroll', this.boundOnScroll);
      } catch { /* ignore */ }
      this.boundOnScroll = null;
    }
    if (this.isScrollingCarousel) {
    // passive listener so we don't block scrolling
      this.boundOnScroll = this.onScroll.bind(this);
      this.unlistenScroll = this.renderer.listen(this.containerElement, 'scroll', this.boundOnScroll);
      this.renderer.setStyle(this.containerElement, 'overflowX', 'auto');
    } else {
      this.renderer.setStyle(this.containerElement, 'overflowX', 'hidden');
    }
  }

  // setup ResizeObserver to react to image/layout changes that affect sizing
  private setupResizeObserver(): void {
    // disconnect previous
    this.resizeObserver?.disconnect();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      // recalc widths when sizes change
      this.setHasMoreItems();
      this.setMouseScrolling();
      this.updateItemsToShow();
    });

    // observe host and scroll container
    if (this.host && this.host.nativeElement) {
      this.resizeObserver.observe(this.host.nativeElement);
    }
    if (this.scrollContainer && this.containerElement) {
      this.resizeObserver.observe(this.containerElement);
    }
  }
}
